import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { OrgNav } from '../components/OrgNav.js';
import {
  Card,
  EmptyState,
  ErrorBanner,
  InvoiceStatusBadge,
  Loading,
  Spinner,
} from '../components/ui.js';
import { api, type Invoice, type InvoiceItem } from '../lib/api.js';
import { formatDateRange, formatMoney, formatNumber, formatRate } from '../lib/format.js';
import { useAsync } from '../lib/hooks.js';

/** Invoice history, expandable to line items. */
export function InvoicesPage(): ReactNode {
  const { orgId = '' } = useParams();
  const [expanded, setExpanded] = useState<string | null>(null);

  const invoices = useAsync(
    () =>
      api.get<{ data: Invoice[]; pagination: { limit: number; offset: number } }>(
        `/api/organizations/${orgId}/invoices?limit=50`,
      ),
    [orgId],
  );

  if (invoices.loading) return <Loading />;

  const list = invoices.data?.data ?? [];

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <h1>Invoices</h1>
          <p>One invoice per closed billing period.</p>
        </div>
      </div>

      <OrgNav orgId={orgId} />

      <div className="stack">
        <ErrorBanner error={invoices.error} />

        {list.length === 0 ? (
          <Card>
            <EmptyState title="No invoices yet">
              <p className="muted small">
                An invoice is generated when a billing period closes. Until then you can see the
                running cost on the overview.
              </p>
            </EmptyState>
          </Card>
        ) : (
          <Card>
            <table>
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{formatDateRange(invoice.periodStart, invoice.periodEnd)}</td>
                    <td>
                      <InvoiceStatusBadge status={invoice.status} />
                    </td>
                    <td className="num">{formatMoney(invoice.totalMinor, invoice.currency)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="button-ghost"
                        onClick={() => setExpanded(expanded === invoice.id ? null : invoice.id)}
                      >
                        {expanded === invoice.id ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {expanded && <InvoiceDetail orgId={orgId} invoiceId={expanded} />}
      </div>
    </div>
  );
}

function InvoiceDetail({ orgId, invoiceId }: { orgId: string; invoiceId: string }): ReactNode {
  const detail = useAsync(
    () =>
      api.get<{ invoice: Invoice; items: InvoiceItem[] }>(
        `/api/organizations/${orgId}/invoices/${invoiceId}`,
      ),
    [orgId, invoiceId],
  );

  if (detail.loading) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }
  if (detail.error || !detail.data) return <ErrorBanner error={detail.error} />;

  const { invoice, items } = detail.data;

  return (
    <Card title={formatDateRange(invoice.periodStart, invoice.periodEnd)}>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th style={{ textAlign: 'right' }}>Quantity</th>
            <th style={{ textAlign: 'right' }}>Rate</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                {item.description}
                {/* Included usage is listed at zero so the invoice can be checked, not just trusted. */}
                {item.type === 'included' && (
                  <span className="badge" style={{ marginLeft: '0.5rem' }}>
                    Included
                  </span>
                )}
              </td>
              <td className="num">
                {item.quantity === 1 && item.type === 'base' ? '' : formatNumber(item.quantity)}
              </td>
              <td className="num">
                {item.unitPriceMinor === 0
                  ? '—'
                  : formatRate(item.unitPriceMinor, item.unitSize, invoice.currency)}
              </td>
              <td className="num">{formatMoney(item.amountMinor, invoice.currency)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={3}>Subtotal</td>
            <td className="num">{formatMoney(invoice.subtotalMinor, invoice.currency)}</td>
          </tr>
          {invoice.taxMinor > 0 && (
            <tr>
              <td colSpan={3}>Tax</td>
              <td className="num">{formatMoney(invoice.taxMinor, invoice.currency)}</td>
            </tr>
          )}
          <tr>
            <td colSpan={3}>
              <strong>Total</strong>
            </td>
            <td className="num">
              <strong>{formatMoney(invoice.totalMinor, invoice.currency)}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}
